import { useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { EXAM_TYPE_LABEL, STATION_LABEL } from '@dru/types';
import { Link, useParams } from 'react-router-dom';
import type { AppointmentDetail as Shape, AuditRow, CursorPage, ExaminationOrderRow } from '@dru/types';
import { APPOINTMENT_STATUS_LABEL } from '@dru/types';
import {
  AddressBlock, Alert, Button, ContactBlock, DataTable, Detail, DetailHeader, DetailSection, Dialog,
  EmptyState, ErrorState, InfoTip, StatusBadge, TextField, TextSkeleton, useIdempotencyKey, useToast, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { NoShowDialog, RescheduleDialog } from '../components/AppointmentActions';
import { ArrangeDialog } from '../components/ArrangeDialog';
import { caseStatusLabel, caseStatusTone } from './Examinations';
import { describe } from './Dashboard';

const TONE: Record<string, Tone> = {
  SCHEDULED: 'pending', CONFIRMED: 'pending', CHECKED_IN: 'covered', COMPLETED: 'covered',
  NO_SHOW: 'overdue', CANCELLED: 'neutral', RESCHEDULED: 'neutral',
};
const STATION_STATE: Record<string, { label: string; tone: Tone }> = {
  COMPLETED: { label: 'Done', tone: 'covered' },
  SKIPPED: { label: 'Skipped', tone: 'neutral' },
  PENDING: { label: 'Not yet', tone: 'pending' },
  IN_PROGRESS: { label: 'In progress', tone: 'pending' },
};
const when = (v: string | null | undefined) => (v ? formatDateTime(v) : '—');
const humanize = (v?: string | null) => (v ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ') : '—');
const apptLabel = (s: string) => APPOINTMENT_STATUS_LABEL[s] ?? humanize(s);

/**
 * One booked slot: who, when and where (with directions), what state it is
 * in and what can still be done to it, what it replaced, and how far the
 * visit got. The check-in pass itself is never shown again — only its
 * prefix, for support to match a worker's copy.
 */
export function AppointmentDetail() {
  const { id = '' } = useParams();
  const { permissions } = useAuth();
  const can = (p: string) => permissions.has(p);
  const { toast } = useToast();
  const canRead = can('appointment.read');
  const canAudit = can('audit.read');
  const appt = useQuery<Shape | null>(() => (canRead ? api.get(`/appointments/${id}`) : Promise.resolve(null)), [id, canRead]);
  // The activity list reads the audit trail, which is its own permission.
  const trail = useQuery<CursorPage<AuditRow> | null>(
    () => (canAudit ? api.get(`/audit?entityType=Appointment&entityId=${id}&limit=50`) : Promise.resolve(null)),
    [id, canAudit],
  );
  const [acting, setActing] = useState<null | 'cancel' | 'move' | 'rebook' | 'no-show'>(null);
  // A cancelled slot leaves its case due: book the next one from here.
  const [rebooking, setRebooking] = useState<ExaminationOrderRow | null>(null);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const a = appt.data;
  async function bookAgain(orderId: string) {
    setOpening(true); setOpenError(null);
    try { setRebooking(await api.get<ExaminationOrderRow>(`/examinations/${orderId}`)); }
    catch (e) { setOpenError(errorMessage(e)); } finally { setOpening(false); }
  }

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Appointment" />
        <EmptyState title="Appointments are not part of your role" hint="Ask your company administrator if you need to follow bookings." />
      </div>
    );
  }
  if (appt.error) {
    return (
      <div className="space-y-4">
        <ErrorState title="This appointment could not be opened" detail={appt.error} onRetry={appt.reload} />
      </div>
    );
  }

  const booked = a ? ['SCHEDULED', 'CONFIRMED'].includes(a.status) : false;
  const passed = a ? new Date(a.scheduledFor).getTime() < Date.now() : false;
  const canRebook = a?.status === 'NO_SHOW' && a.order.status === 'NO_SHOW_OPEN' && can('appointment.reschedule');
  const canBookAgain = a?.status === 'CANCELLED' && !a.replacedBy
    && ['REQUESTED', 'ROUTED', 'CONSENT_PENDING'].includes(a.order.status) && can('appointment.create');

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Appointment"
        title={a ? a.order.employee.fullName : 'Loading…'}
        badges={a && <StatusBadge tone={TONE[a.status] ?? 'neutral'}>{apptLabel(a.status)}</StatusBadge>}
        subtitle={a && [
          formatDateTime(a.scheduledFor),
          a.venue?.name ?? a.partner.name,
          a.venueType === 'CAMP' ? 'camp visit' : null,
          (EXAM_TYPE_LABEL[a.order.examinationType] ?? a.order.examinationType),
        ].filter(Boolean).join(' · ')}
        actions={a && (
          <>
            {canRebook && <Button onClick={() => setActing('rebook')}>Rebook</Button>}
            {canBookAgain && <Button onClick={() => bookAgain(a.order.id)} loading={opening}>Book a new slot</Button>}
            {booked && passed && can('appointment.noshow') && (
              <Button onClick={() => setActing('no-show')}>Did not attend</Button>
            )}
            {booked && can('appointment.reschedule') && (
              <Button variant="secondary" onClick={() => setActing('move')}>Reschedule</Button>
            )}
            {booked && can('appointment.cancel') && (
              <Button variant="secondary" className="text-danger-600" onClick={() => setActing('cancel')}>Cancel the slot</Button>
            )}
          </>
        )}
      />

      {!a && <TextSkeleton lines={6} />}
      {openError && <Alert>{openError}</Alert>}

      {a?.status === 'NO_SHOW' && (
        <Alert tone="warning" title="The employee did not attend">
          {a.order.status === 'NO_SHOW_OPEN'
            ? 'The medical case stays open until the employee is rebooked.'
            : 'This slot was missed. The medical case has moved on since.'}
        </Alert>
      )}
      {a?.status === 'CANCELLED' && (
        <Alert tone="info" title="This slot was cancelled">
          {a.cancelReason ? `${a.cancelReason}. ` : ''}The medical case stays due until a new slot is booked.
          {a.replacedBy ? ' A new slot has been booked.' : ''}
        </Alert>
      )}

      {a && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DetailSection title="Employee and medical case" className="min-w-0">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Employee" value={<Link to={`/hr/employees/${a.order.employee.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">{a.order.employee.fullName}</Link>} />
              <Detail label="Employee ID" value={a.order.employee.employeeCode} mono />
              <Detail label="Mobile" value={a.order.employee.mobile ?? '—'} mono />
              <Detail
                label="Medical case"
                value={(
                  <span className="flex flex-wrap items-center gap-2">
                    <Link to={`/hr/examinations/${a.order.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">
                      {EXAM_TYPE_LABEL[a.order.examinationType] ?? a.order.examinationType}
                    </Link>
                    <StatusBadge tone={caseStatusTone(a.order.status)}>{caseStatusLabel(a.order.status)}</StatusBadge>
                  </span>
                )}
              />
              <Detail label="Priority" value={humanize(a.order.priority)} />
              <Detail label="Due" value={a.order.dueDate ? formatDate(a.order.dueDate) : '—'} />
              <Detail label="Campaign" value={a.order.campaignId ? <Link to={`/hr/campaigns/${a.order.campaignId}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">Part of a campaign</Link> : 'Raised individually'} />
              <Detail label="Check-in code" value={a.checkInTokenPrefix ? `${a.checkInTokenPrefix}… (first characters only)` : 'Not issued'} mono />
            </dl>
          </DetailSection>

          <DetailSection title={a.venue?.historical ? 'Venue, as it was when booked' : 'Venue'} className="min-w-0">
            {a.venue ? (
              <>
                <p className="text-body font-semibold text-neutral-900">{a.venue.name}</p>
                <AddressBlock address={a.venue} className="mt-1" showMapLink />
                <div className="mt-3">
                  <ContactBlock contact={{ contactName: a.venue.contactName, contactPhone: a.venue.contactPhone, emergencyPhone: a.venue.emergencyPhone }} />
                </div>
                {(a.venue.operatingDays || a.venue.operatingHours) && (
                  <p className="mt-2 text-caption text-neutral-500">Open {[a.venue.operatingDays, a.venue.operatingHours].filter(Boolean).join(' · ')}</p>
                )}
              </>
            ) : <p className="text-small text-neutral-500">Venue not recorded.</p>}
            {/* The network partner's record is an administrator's screen, not
                one this console can open — so its name, not a link. */}
            <p className="mt-2 text-caption text-neutral-500">
              Run by {a.partner.name} <span className="font-mono">{a.partner.code}</span>
            </p>
          </DetailSection>

          <DetailSection title="Slot" className="min-w-0">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Scheduled for" value={when(a.scheduledFor)} />
              <Detail label="Length" value={`${a.slotMinutes} minutes`} />
              <Detail label="Booked" value={when(a.createdAt)} />
              <Detail label="Checked in" value={when(a.checkedInAt)} />
              {a.cancelReason && <Detail label="Cancelled because" value={a.cancelReason} className="sm:col-span-2" />}
              {a.replaced && <Detail label="Replaces" value={<Link to={`/hr/appointments/${a.replaced.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">{when(a.replaced.scheduledFor)} ({apptLabel(a.replaced.status).toLowerCase()})</Link>} />}
              {a.replacedBy && <Detail label="Replaced by" value={<Link to={`/hr/appointments/${a.replacedBy.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">{when(a.replacedBy.scheduledFor)} ({apptLabel(a.replacedBy.status).toLowerCase()})</Link>} />}
            </dl>
          </DetailSection>

          <DetailSection
            title="Visit"
            className="min-w-0"
            action={a.encounter ? (
              <InfoTip label="About the visit" className="mr-auto self-center">
                Stations say done or not done. What was found stays in the clinical record.
              </InfoTip>
            ) : undefined}
          >
            {!a.encounter ? <p className="text-small text-neutral-500">The employee has not checked in.</p> : (
              <>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Detail label="Checked in" value={when(a.encounter.checkedInAt)} />
                  <Detail label="Completed" value={a.encounter.completedAt ? `${when(a.encounter.completedAt)}${a.encounter.completionType ? ` (${humanize(a.encounter.completionType).toLowerCase()})` : ''}` : 'Still at the venue'} />
                </dl>
                <ul className="mt-3 divide-y divide-neutral-100">
                  {a.encounter.stations.map((st) => {
                    const state = STATION_STATE[st.status] ?? { label: humanize(st.status), tone: 'pending' as Tone };
                    return (
                      <li key={st.id} className="flex items-center justify-between py-1.5 text-small">
                        <span className="text-neutral-800">{STATION_LABEL[st.station] ?? humanize(st.station)}</span>
                        <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </DetailSection>

          {canAudit && (
            <DetailSection title="Activity" className="min-w-0 lg:col-span-2">
              <DataTable<AuditRow> rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload} rowKey={(r) => r.id}
                empty={{ title: 'Nothing recorded yet', hint: 'Every change to this booking is written here.' }}
                columns={[
                  { key: 'what', header: 'What', cell: (r) => <span className="text-neutral-900">{describe(r.action)}</span> },
                  { key: 'when', header: 'When', cell: (r) => <span className="text-small text-neutral-600">{formatDateTime(r.occurredAt)}</span> },
                  { key: 'outcome', header: 'Outcome', mobileLabel: false, cell: (r) => <StatusBadge tone={r.outcome === 'SUCCESS' ? 'covered' : 'overdue'}>{r.outcome === 'SUCCESS' ? 'Done' : 'Refused'}</StatusBadge> },
                ]} />
            </DetailSection>
          )}
        </div>
      )}

      {a && acting === 'cancel' && (
        <CancelDialog appointment={a} onClose={() => setActing(null)}
          onDone={() => { setActing(null); appt.reload(); toast('info', 'Slot cancelled. The medical case stays due.'); }} />
      )}
      {a && (acting === 'move' || acting === 'rebook') && (
        <RescheduleDialog
          appointmentId={a.id}
          partnerId={a.partnerId}
          partnerName={a.venue?.name ?? a.partner.name}
          who={a.order.employee.fullName}
          scheduledFor={a.scheduledFor}
          mode={acting}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); appt.reload(); toast('success', acting === 'rebook' ? 'Rebooked.' : 'Appointment moved.'); }}
        />
      )}
      {rebooking && (
        <ArrangeDialog
          order={rebooking}
          onClose={() => setRebooking(null)}
          onDone={() => { setRebooking(null); appt.reload(); toast('success', 'New slot booked.'); }}
        />
      )}
      {a && acting === 'no-show' && (
        <NoShowDialog
          appointmentId={a.id}
          who={a.order.employee.fullName}
          when={a.scheduledFor}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); appt.reload(); toast('info', 'Recorded as not attended.'); }}
        />
      )}
    </div>
  );
}

function CancelDialog({ appointment: a, onClose, onDone }: { appointment: Shape; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kept across a retry, so a double submit is one cancellation.
  const idem = useIdempotencyKey();
  async function submit() {
    setBusy(true); setError(null);
    try { await api.post(`/appointments/${a.id}/cancel`, { reason: reason.trim() }, idem.key()); onDone(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  return (
    <Dialog title="Cancel this slot" onClose={onClose}>
      <p className="mt-1 text-small text-neutral-600">
        Releases {a.order.employee.fullName}&rsquo;s slot at {a.venue?.name ?? a.partner.name} on{' '}
        {formatDateTime(a.scheduledFor)}, and tells them not to attend.
      </p>
      <Alert tone="info" title="The medical case stays due" className="mt-3">
        Only the booking is released. The employee still needs a new slot.
      </Alert>
      <TextField label="Reason" value={reason} onChange={setReason} autoFocus required className="mt-3"
        placeholder="Venue closed, employee unavailable, booked in error…"
        hint="At least five characters."
        info="Recorded in the audit trail." />
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Keep it</Button>
        <Button variant="danger" onClick={submit} loading={busy} disabled={reason.trim().length < 5}>Cancel the slot</Button>
      </div>
    </Dialog>
  );
}
